import React, { useCallback, useState, useEffect } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';

const MAP_LIBRARIES: ("places")[] = ["places"];
import axios from 'axios';
import { Search, Loader, MapPin, X } from 'lucide-react';
import './styles/MapComponent.css';

interface MapComponentProps {
  latitude: number;
  longitude: number;
  locationName?: string;
  onLocationChange?: (lat: number, lng: number) => void;
}

const MapComponent: React.FC<MapComponentProps> = ({
  latitude,
  longitude,
  locationName = 'Your Location',
  onLocationChange,
}) => {
  const [apiKey] = useState<string>(import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '');
  const [loading, setLoading] = useState(!import.meta.env.VITE_GOOGLE_MAPS_API_KEY);
  const [error, setError] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: latitude, lng: longitude });
  const [markerPosition, setMarkerPosition] = useState({ lat: latitude, lng: longitude });
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [scriptError] = useState<string>('');
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries: MAP_LIBRARIES,
    id: 'maps-script-loader',
  });

  useEffect(() => {
    setMapCenter({ lat: latitude, lng: longitude });
    setMarkerPosition({ lat: latitude, lng: longitude });
  }, [latitude, longitude]);

  useEffect(() => {
    if (apiKey) {
      setLoading(false);
    }
  }, [apiKey]);

  useEffect(() => {
    if (!apiKey) {
      setError('Google Maps API key missing');
    } else {
      setError('');
      setLoading(false);
    }
  }, [apiKey]);

  const onMapLoad = useCallback(() => {
  }, []);

  const onMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      if (onLocationChange) {
        onLocationChange(lat, lng);
      }
    }
  }, [onLocationChange]);

  const onMarkerDragEnd = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setMarkerPosition({ lat, lng });
      if (onLocationChange) {
        onLocationChange(lat, lng);
      }
    }
  }, [onLocationChange]);

  const handleGoToCurrentLocation = useCallback(() => {
    if (navigator.geolocation) {
      setSearchLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setMapCenter({ lat, lng });
          setMarkerPosition({ lat, lng });
          setCurrentLocation({ lat, lng });
          setSearchQuery('');
          setSearchSuggestions([]);
          if (onLocationChange) {
            onLocationChange(lat, lng);
          }
          setSearchLoading(false);
        },
        (error) => {
          console.error('Error getting current location:', error);
          setSearchLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      );
    }
  }, [onLocationChange]);

  const searchLocations = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchSuggestions([]);
      return;
    }

    setSearchLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://backend.buildsupplyco.com/api/v1';
      const response = await axios.get(`${apiUrl}/location/place-autocomplete`, {
        params: {
          input: query,
        },
      });

      if (response.data.success && response.data.data) {
        setSearchSuggestions(response.data.data);
      }
    } catch (err) {
      console.error('Error searching locations:', err);
      setSearchSuggestions([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleLocationSelect = useCallback(
    async (prediction: any) => {
      setSearchQuery(prediction.description);
      setSearchSuggestions([]);

      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'https://backend.buildsupplyco.com/api/v1';
        const response = await axios.get(`${apiUrl}/location/place-details/${prediction.placeId}`);

        if (response.data.success && response.data.data) {
          const location = response.data.data.geometry?.location;
          if (location) {
            const lat = location.lat;
            const lng = location.lng;
            setMapCenter({ lat, lng });
            setMarkerPosition({ lat, lng });
            if (onLocationChange) {
              onLocationChange(lat, lng);
            }
          }
        }
      } catch (err) {
        console.error('Error getting place details:', err);
      }
    },
    [onLocationChange]
  );

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      searchLocations(query);
    } else {
      setSearchSuggestions([]);
    }
  };

  if (loading || (!isLoaded && !loadError)) {
    return (
      <div className="map-loading">
        Loading map...
      </div>
    );
  }

  if (error || scriptError || loadError || !apiKey) {
    return (
      <div
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          color: '#d32f2f',
          fontSize: '14px',
          padding: '20px',
          textAlign: 'center',
        }}
      >
        {(loadError && 'Failed to load Google Maps script') || scriptError || error || 'Google Maps API key not available'}
      </div>
    );
  }

  return (
      <div className="map-root">
        {/* Search Bar */}
        <div className="map-search">
          <div className="map-search-inner">
            <div className="map-search-input-row">
              <Search size={18} style={{ color: '#999', flexShrink: 0 }} />
              <input className="map-search-input" type="text" placeholder="Search location..." value={searchQuery} onChange={(e) => handleSearchChange(e.target.value)} />
              {searchQuery && (
                <button className="map-search-clear" onClick={() => { setSearchQuery(''); setSearchSuggestions([]); }}>
                  <X size={18} />
                </button>
              )}
            </div>

            {/* Search Suggestions */}
            {searchSuggestions.length > 0 && (
              <div className="map-search-suggestions">
                {searchSuggestions.map((suggestion, index) => (
                  <button key={index} onClick={() => handleLocationSelect(suggestion)} className="map-suggestion">
                    <div className="map-suggestion-row">
                      <MapPin size={14} style={{ color: '#999', flexShrink: 0 }} />
                      <div className="map-suggestion-text">{suggestion.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {searchLoading && searchQuery && (
              <div className="map-search-loading">
                <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                Searching...
              </div>
            )}
          </div>
        </div>

        {/* Go to Current Location Button - centered pill style */}
        <div className="map-current-wrapper">
          <button onClick={handleGoToCurrentLocation} disabled={searchLoading} className="map-current-btn">
            {searchLoading ? (
              <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <span className="map-current-icon">
                <MapPin size={16} />
              </span>
            )}
            Go to current location
          </button>
        </div>

        <GoogleMap
          mapContainerClassName="map-container"
          center={mapCenter}
          zoom={17}
          onLoad={onMapLoad}
          onClick={onMapClick}
          options={{
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: true,
            fullscreenControl: true,
          }}
        >
          {currentLocation && (
            <Marker
              position={currentLocation}
              clickable={false}
              draggable={false}
              title={"Your current location"}
              // Use Google Maps default marker
            />
          )}
          <Marker
            position={markerPosition}
            title={locationName}
            draggable={true}
            onDragEnd={onMarkerDragEnd}
            // Use Google Maps default marker
          />
        </GoogleMap>
      </div>
  );
};

export default MapComponent;
